import React from 'react'
import { Query, Mutation } from 'react-apollo'
import { Button, TextInput } from 'carbon-components-react'

import { GET_MY_SSO_PROVIDERS } from '../graphql/queries'
import { UNLINK_SSO, SET_PASSWORD, DO_SSO } from '../graphql/mutations'
import ApolloClient from '../utils/Apollo'

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID

// Dynamically load the Google Identity Services script
let googleScriptLoaded = false
function loadGoogleScript() {
  if (googleScriptLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (document.getElementById('google-gis-script')) {
      googleScriptLoaded = true
      resolve()
      return
    }
    const script = document.createElement('script')
    script.id = 'google-gis-script'
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => { googleScriptLoaded = true; resolve() }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

// Dynamically load the Facebook SDK
let fbScriptLoaded = false
function loadFacebookScript() {
  if (fbScriptLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (document.getElementById('facebook-jssdk')) {
      fbScriptLoaded = true
      resolve()
      return
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: 'v18.0'
      })
      fbScriptLoaded = true
      resolve()
    }
    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.onerror = reject
    document.head.appendChild(script)
  })
}

// Dynamically load the Apple Sign In JS SDK
let appleScriptLoaded = false
function loadAppleScript() {
  if (appleScriptLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (document.getElementById('apple-signin-script')) {
      appleScriptLoaded = true
      resolve()
      return
    }
    const script = document.createElement('script')
    script.id = 'apple-signin-script'
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.onload = () => { appleScriptLoaded = true; resolve() }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

const PROVIDER_ICONS = {
  google: (
    <svg viewBox='0 0 24 24' width='20' height='20'>
      <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z'/>
      <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/>
      <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/>
      <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/>
    </svg>
  ),
  facebook: (
    <svg viewBox='0 0 24 24' width='20' height='20'>
      <path fill='#1877F2' d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'/>
    </svg>
  ),
  apple: (
    <svg viewBox='0 0 24 24' width='20' height='20'>
      <path fill='#FFFFFF' d='M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.62-2.2.44-3.06-.4C3.79 16.17 4.36 9.53 8.8 9.29c1.28.06 2.15.72 2.9.76.97-.2 1.9-.9 2.98-.81 1.41.11 2.48.66 3.18 1.67-2.86 1.72-2.18 5.52.49 6.58-.6 1.53-1.37 3.04-2.3 3.79zM12.05 9.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z'/>
    </svg>
  )
}

const PROVIDERS = [
  { driver: 'google', label: 'Google', available: !!GOOGLE_CLIENT_ID },
  { driver: 'facebook', label: 'Facebook', available: !!FACEBOOK_APP_ID },
  { driver: 'apple', label: 'Apple', available: !!APPLE_CLIENT_ID }
]

class ConnectedAccounts extends React.PureComponent {
  state = {
    connecting: null,
    showSetPassword: false,
    password: '',
    passwordConfirmation: '',
    passwordErrors: {}
  }

  handleConnect = (driver) => {
    if (driver === 'google') {
      this.connectGoogle()
    } else if (driver === 'facebook') {
      this.connectFacebook()
    } else if (driver === 'apple') {
      this.connectApple()
    }
  }

  connectGoogle = () => {
    this.setState({ connecting: 'google' })
    loadGoogleScript()
      .then(() => {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          callback: (response) => {
            if (response.error) {
              this.setState({ connecting: null })
              return
            }
            this.linkProvider('google', response.access_token)
          },
          error_callback: () => {
            this.setState({ connecting: null })
          }
        })
        client.requestAccessToken()
      })
      .catch(() => {
        this.setState({ connecting: null })
        this.props.alert('Failed to load Google sign-in.')
      })
  }

  connectFacebook = () => {
    this.setState({ connecting: 'facebook' })
    loadFacebookScript()
      .then(() => {
        window.FB.login(function (response) {
          if (response.authResponse) {
            this.linkProvider('facebook', response.authResponse.accessToken)
          } else {
            this.setState({ connecting: null })
          }
        }.bind(this), { scope: 'email,public_profile' })
      })
      .catch(() => {
        this.setState({ connecting: null })
        this.props.alert('Failed to load Facebook sign-in.')
      })
  }

  connectApple = () => {
    this.setState({ connecting: 'apple' })
    loadAppleScript()
      .then(() => {
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || 'https://develop.api.vevacollect.com/auth/apple/callback',
          usePopup: true
        })
        return window.AppleID.auth.signIn()
      })
      .then((response) => {
        this.linkProvider('apple', response.authorization.id_token)
      })
      .catch((err) => {
        if (err && err.error === 'popup_closed_by_user') {
          this.setState({ connecting: null })
          return
        }
        this.setState({ connecting: null })
        this.props.alert('Apple sign-in failed. Please try again.')
      })
  }

  linkProvider = (driver, accessToken) => {
    ApolloClient.mutate({
      mutation: DO_SSO,
      variables: { driver, accessToken }
    }).then(({ data }) => {
      this.setState({ connecting: null })
      if (data.doSSO.success) {
        ApolloClient.query({
          query: GET_MY_SSO_PROVIDERS,
          fetchPolicy: 'network-only'
        })
      } else {
        this.props.alert(data.doSSO.message || 'Failed to link account.')
      }
    }).catch(() => {
      this.setState({ connecting: null })
      this.props.alert('Failed to link account. Please try again.')
    })
  }

  handleDisconnect = (driver, unlinkSSO, providers) => {
    const isLastProvider = providers.length === 1
    if (isLastProvider && !this.props.hasPassword) {
      this.setState({ showSetPassword: true, pendingUnlinkDriver: driver })
      return
    }

    unlinkSSO({ variables: { driver } })
  }

  handleSetPassword = (setPassword) => {
    const { password, passwordConfirmation } = this.state
    const errors = {}

    if (!password || password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    }
    if (password !== passwordConfirmation) {
      errors.passwordConfirmation = 'Passwords do not match'
    }
    if (Object.keys(errors).length > 0) {
      this.setState({ passwordErrors: errors })
      return
    }

    setPassword({
      variables: { password, passwordConfirmation }
    }).then(({ data }) => {
      if (data.setPassword.success) {
        this.setState({
          showSetPassword: false,
          password: '',
          passwordConfirmation: '',
          passwordErrors: {}
        })
        this.props.alert('Password set successfully. You can now disconnect the provider.', {
          title: 'Password Set'
        })
      } else {
        this.props.alert(data.setPassword.message || 'Failed to set password.')
      }
    }).catch(() => {
      this.props.alert('Failed to set password. Please try again.')
    })
  }

  render() {
    const { connecting, showSetPassword, password, passwordConfirmation, passwordErrors } = this.state

    return (
      <Query query={GET_MY_SSO_PROVIDERS} fetchPolicy='network-only'>
        {({ loading, data }) => {
          if (loading) return null

          const providers = (data && data.mySSOProviders) || []

          return (
            <Mutation
              mutation={UNLINK_SSO}
              refetchQueries={[{ query: GET_MY_SSO_PROVIDERS }]}
              onCompleted={(data) => {
                if (!data.unlinkSSO.success) {
                  this.props.alert(data.unlinkSSO.message)
                }
              }}
              onError={() => {
                this.props.alert('Failed to disconnect account.')
              }}
            >
              {(unlinkSSO, { loading: unlinking }) => (
                <div className='connected-accounts'>
                  {PROVIDERS.filter(p => p.available).map(({ driver, label }) => {
                    const linked = providers.find(p => p.driver === driver)
                    return (
                      <div className='connected-account' key={driver}>
                        <div className='connected-account__icon'>
                          {PROVIDER_ICONS[driver]}
                        </div>
                        <div className='connected-account__info'>
                          <span className='connected-account__provider'>{label}</span>
                          <span className='connected-account__email'>
                            {linked ? linked.providerEmail : 'Not connected'}
                          </span>
                        </div>
                        <div className='connected-account__action'>
                          {linked ? (
                            <Button
                              kind='secondary'
                              small
                              className='connected-account__disconnect-btn'
                              disabled={unlinking}
                              onClick={() => this.handleDisconnect(driver, unlinkSSO, providers)}
                            >
                              Disconnect
                            </Button>
                          ) : (
                            <Button
                              kind='secondary'
                              small
                              disabled={!!connecting}
                              onClick={() => this.handleConnect(driver)}
                            >
                              {connecting === driver ? 'Connecting...' : 'Connect'}
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}

                  {showSetPassword && (
                    <Mutation mutation={SET_PASSWORD}>
                      {(setPassword, { loading: settingPassword }) => (
                        <div className='connected-account__set-password'>
                          <p className='connected-account__warning'>
                            You need to set a password before disconnecting your last sign-in provider,
                            otherwise you won't be able to log in.
                          </p>
                          <div className='formRow'>
                            <TextInput
                              id='setPassword'
                              name='password'
                              labelText='New Password'
                              type='password'
                              value={password}
                              onChange={(e) => this.setState({ password: e.target.value, passwordErrors: {} })}
                              invalid={!!passwordErrors.password}
                              invalidText={passwordErrors.password || ''}
                            />
                          </div>
                          <div className='formRow'>
                            <TextInput
                              id='setPasswordConfirmation'
                              name='passwordConfirmation'
                              labelText='Confirm Password'
                              type='password'
                              value={passwordConfirmation}
                              onChange={(e) => this.setState({ passwordConfirmation: e.target.value, passwordErrors: {} })}
                              invalid={!!passwordErrors.passwordConfirmation}
                              invalidText={passwordErrors.passwordConfirmation || ''}
                            />
                          </div>
                          <div className='connected-account__set-password-actions'>
                            <Button
                              small
                              disabled={settingPassword}
                              onClick={() => this.handleSetPassword(setPassword)}
                            >
                              {settingPassword ? 'Setting...' : 'Set Password'}
                            </Button>
                            <button
                              type='button'
                              className='connected-account__cancel'
                              onClick={() => this.setState({ showSetPassword: false, password: '', passwordConfirmation: '', passwordErrors: {} })}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </Mutation>
                  )}
                </div>
              )}
            </Mutation>
          )
        }}
      </Query>
    )
  }
}

export default ConnectedAccounts
